import { FC } from 'react'
import styled, { keyframes } from 'styled-components'

const TipsterSubscriptionsSkeleton: FC = () => {
  return (
    <SkeletonContainer>
      {/* Channel cards skeleton */}
      {Array.from({ length: 2 }).map((_, index) => (
        <SkeletonChannelCard key={index}>
          {/* Channel header */}
          <SkeletonChannelHeader>
            <SkeletonChannelLogo />
            <SkeletonChannelInfo>
              <SkeletonChannelName />
              <SkeletonChannelSubtext />
            </SkeletonChannelInfo>
            <SkeletonMenuIcon />
          </SkeletonChannelHeader>

          {/* Payment tags */}
          <SkeletonTagsRow>
            <SkeletonTag width="60px" />
            <SkeletonTag width="80px" />
            <SkeletonTag width="70px" />
          </SkeletonTagsRow>

          {/* Subscription plans */}
          <SkeletonSubscriptionPlan>
            <SkeletonPlanInfo>
              <SkeletonPlanName />
              <SkeletonPlanDuration />
            </SkeletonPlanInfo>
            <SkeletonPlanPrice />
          </SkeletonSubscriptionPlan>

          <SkeletonSubscriptionPlan>
            <SkeletonPlanInfo>
              <SkeletonPlanName />
              <SkeletonPlanDuration />
            </SkeletonPlanInfo>
            <SkeletonPlanPrice />
          </SkeletonSubscriptionPlan>
        </SkeletonChannelCard>
      ))}
    </SkeletonContainer>
  )
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    var(--zinc-800) 25%,
    var(--zinc-700) 50%,
    var(--zinc-800) 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
`

const SkeletonContainer = styled.div`
  width: 100%;
  padding: 0 15px;
  padding-top: 100px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const SkeletonChannelCard = styled.div`
  background: var(--bg-secondary);
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.11);
`

const SkeletonChannelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const SkeletonChannelLogo = styled(SkeletonBase)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  flex-shrink: 0;
`

const SkeletonChannelInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkeletonChannelName = styled(SkeletonBase)`
  width: 120px;
  height: 20px;
`

const SkeletonChannelSubtext = styled(SkeletonBase)`
  width: 80px;
  height: 14px;
`

const SkeletonMenuIcon = styled(SkeletonBase)`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
`

const SkeletonTagsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const SkeletonTag = styled(SkeletonBase)<{ width?: string }>`
  width: ${({ width }) => width || '60px'};
  height: 24px;
  border-radius: 12px;
`

const SkeletonSubscriptionPlan = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--bg-tertiary);

  &:last-child {
    border-bottom: none;
  }
`

const SkeletonPlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SkeletonPlanName = styled(SkeletonBase)`
  width: 140px;
  height: 18px;
`

const SkeletonPlanDuration = styled(SkeletonBase)`
  width: 80px;
  height: 14px;
`

const SkeletonPlanPrice = styled(SkeletonBase)`
  width: 70px;
  height: 20px;
  border-radius: 8px;
`

export default TipsterSubscriptionsSkeleton
